// Procedural low-poly geometry toolkit shared by characters, weapons, hands and equipment.
//
// A MeshBuilder collects flat-shaded triangles per "material key" (any string) and builds one merged
// non-indexed BufferGeometry per key. Primitives: bevelled box, lathe / cylinder / sphere, and a convex
// side-profile prism. All primitives honour an optional euler rotation and a nested transform (`at`).
// Pure math: no DOM, no WebGL, so it runs in node (vitest) as well.

import * as THREE from "three";

export type V3 = readonly [number, number, number];
export type V2 = readonly [number, number];
export type Axis = "x" | "y" | "z";

export interface PartOpts {
  /** Euler XYZ rotation in radians, applied around the part centre before translating. */
  rot?: V3;
  /** Vertex colour (hex); only written when build({ vertexColors: true }). Default white. */
  color?: string;
}

export interface BoxOpts extends PartOpts {
  /** Chamfer size in metres (0 = plain 12-triangle box, > 0 = 44 triangles). */
  bevel?: number;
}

export interface CylOpts extends PartOpts {
  segments?: number;
  /** Close both ends (default true). */
  caps?: boolean;
}

export interface BuildOpts {
  /** Add a `color` attribute from the per-primitive colours. */
  vertexColors?: boolean;
  /** Metres per UV repeat per key (box projection from object space). Default 1. */
  uvMeters?: Readonly<Record<string, number>>;
}

interface Bucket {
  pos: number[];
  nor: number[];
  col: number[];
}

const ONE = new THREE.Vector3(1, 1, 1);
const _e1 = new THREE.Vector3();
const _e2 = new THREE.Vector3();
const _n = new THREE.Vector3();
const _p = new THREE.Vector3();
const _hint = new THREE.Vector3();

const colorCache = new Map<string, readonly [number, number, number]>();

/** Linear-space rgb floats of a sRGB hex colour (what vertex colour attributes expect). */
export function linearRgb(hex: string): readonly [number, number, number] {
  let c = colorCache.get(hex);
  if (c === undefined) {
    const col = new THREE.Color(hex);
    c = [col.r, col.g, col.b];
    colorCache.set(hex, c);
  }
  return c;
}

export class MeshBuilder {
  private readonly buckets = new Map<string, Bucket>();
  private cur = new THREE.Matrix4();
  private readonly m = new THREE.Matrix4();
  private color: readonly [number, number, number] = [1, 1, 1];

  /** Runs `fn` with every primitive offset/rotated by `pos`/`rot` (nestable). */
  at(pos: V3, fn: () => void, rot?: V3): void {
    const prev = this.cur;
    this.cur = prev.clone().multiply(this.localMatrix(pos, rot));
    try {
      fn();
    } finally {
      this.cur = prev;
    }
  }

  /** Bevelled (or plain) box centred at `pos`. */
  box(key: string, size: V3, pos: V3, opts: BoxOpts = {}): void {
    const hx = size[0] / 2;
    const hy = size[1] / 2;
    const hz = size[2] / 2;
    const b = Math.min(opts.bevel ?? 0, Math.min(hx, hy, hz) * 0.9);
    this.begin(pos, opts);
    const bucket = this.bucket(key);
    const vert = (s: readonly number[], axis: number): THREE.Vector3 =>
      new THREE.Vector3(
        s[0] * (axis === 0 ? hx : hx - b),
        s[1] * (axis === 1 ? hy : hy - b),
        s[2] * (axis === 2 ? hz : hz - b),
      );
    const quad = (a: THREE.Vector3, bq: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): void => {
      _hint.copy(a).add(bq).add(c).add(d).multiplyScalar(0.25);
      const h = _hint.clone();
      this.emit(bucket, a, bq, c, h, false);
      this.emit(bucket, a, c, d, h, false);
    };
    const ring: ReadonlyArray<readonly [number, number]> = [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ];
    // Faces
    for (let a = 0; a < 3; a++) {
      const b1 = (a + 1) % 3;
      const c1 = (a + 2) % 3;
      for (const s of [-1, 1]) {
        const v = ring.map(([sb, sc]) => {
          const sg = [0, 0, 0];
          sg[a] = s;
          sg[b1] = sb;
          sg[c1] = sc;
          return vert(sg, a);
        });
        quad(v[0], v[1], v[2], v[3]);
      }
    }
    if (b > 1e-5) {
      // Edge chamfers
      for (let e = 0; e < 3; e++) {
        const a = (e + 1) % 3;
        const bb = (e + 2) % 3;
        for (const sa of [-1, 1]) {
          for (const sb of [-1, 1]) {
            const s1 = [0, 0, 0];
            const s2 = [0, 0, 0];
            s1[e] = -1;
            s2[e] = 1;
            s1[a] = s2[a] = sa;
            s1[bb] = s2[bb] = sb;
            quad(vert(s1, a), vert(s2, a), vert(s2, bb), vert(s1, bb));
          }
        }
      }
      // Corner triangles
      for (const sx of [-1, 1]) {
        for (const sy of [-1, 1]) {
          for (const sz of [-1, 1]) {
            const s = [sx, sy, sz];
            const v0 = vert(s, 0);
            const v1 = vert(s, 1);
            const v2 = vert(s, 2);
            const h = v0.clone().add(v1).add(v2).multiplyScalar(1 / 3);
            this.emit(bucket, v0, v1, v2, h, false);
          }
        }
      }
    }
  }

  /**
   * Surface of revolution around the part's Y axis (or `axis`). `profile` is (radius, height) pairs
   * ordered bottom to top along the outer silhouette; radius 0 points close the ends.
   */
  lathe(
    key: string,
    profile: ReadonlyArray<V2>,
    segments: number,
    pos: V3,
    opts: PartOpts & { axis?: Axis } = {},
  ): void {
    const axisQ = new THREE.Quaternion();
    if (opts.axis === "z") axisQ.setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
    else if (opts.axis === "x") axisQ.setFromEuler(new THREE.Euler(0, 0, -Math.PI / 2));
    this.begin(pos, opts, axisQ);
    const bucket = this.bucket(key);
    const n = Math.max(3, Math.floor(segments));
    const rings: THREE.Vector3[][] = profile.map(([r, y]) => {
      const row: THREE.Vector3[] = [];
      for (let i = 0; i <= n; i++) {
        const t = (i / n) * Math.PI * 2;
        row.push(new THREE.Vector3(r * Math.cos(t), y, r * Math.sin(t)));
      }
      return row;
    });
    for (let k = 0; k + 1 < profile.length; k++) {
      const dr = profile[k + 1][0] - profile[k][0];
      const dy = profile[k + 1][1] - profile[k][1];
      const len = Math.hypot(dr, dy);
      if (len < 1e-9) continue;
      const nr = dy / len;
      const ny = -dr / len;
      for (let i = 0; i < n; i++) {
        const tm = ((i + 0.5) / n) * Math.PI * 2;
        const hint = new THREE.Vector3(nr * Math.cos(tm), ny, nr * Math.sin(tm));
        const v00 = rings[k][i];
        const v01 = rings[k][i + 1];
        const v10 = rings[k + 1][i];
        const v11 = rings[k + 1][i + 1];
        this.emit(bucket, v00, v01, v11, hint, true);
        this.emit(bucket, v00, v11, v10, hint, true);
      }
    }
  }

  /** Cylinder / cone frustum along `axis`, centred at `pos`. `r0` sits at the negative end, `r1` at the positive end. */
  cyl(key: string, axis: Axis, length: number, r0: number, r1: number, pos: V3, opts: CylOpts = {}): void {
    const h = length / 2;
    const profile: V2[] =
      opts.caps === false
        ? [
            [r0, -h],
            [r1, h],
          ]
        : [
            [0, -h],
            [r0, -h],
            [r1, h],
            [0, h],
          ];
    this.lathe(key, profile, opts.segments ?? 8, pos, { axis, rot: opts.rot, color: opts.color });
  }

  /** Low-poly sphere (`segments` around, `rings` pole to pole). */
  sphere(key: string, radius: number, pos: V3, segments = 8, rings = 4, opts: PartOpts = {}): void {
    const profile: V2[] = [];
    for (let j = 0; j <= rings; j++) {
      const phi = -Math.PI / 2 + (j / rings) * Math.PI;
      profile.push([j === 0 || j === rings ? 0 : radius * Math.cos(phi), radius * Math.sin(phi)]);
    }
    this.lathe(key, profile, segments, pos, opts);
  }

  /**
   * Extrudes a convex polygon given in side-view coordinates `(z, y)` along X by `width`
   * (centred on pos.x). Use for stocks, grips, magazines, blades.
   */
  prism(key: string, pts: ReadonlyArray<V2>, width: number, pos: V3, opts: PartOpts = {}): void {
    this.begin(pos, opts);
    const bucket = this.bucket(key);
    const hw = width / 2;
    let cz = 0;
    let cy = 0;
    for (const [z, y] of pts) {
      cz += z;
      cy += y;
    }
    cz /= pts.length;
    cy /= pts.length;
    const left = pts.map(([z, y]) => new THREE.Vector3(-hw, y, z));
    const right = pts.map(([z, y]) => new THREE.Vector3(hw, y, z));
    const hl = new THREE.Vector3(-1, 0, 0);
    const hr = new THREE.Vector3(1, 0, 0);
    for (let i = 1; i + 1 < pts.length; i++) {
      this.emit(bucket, left[0], left[i], left[i + 1], hl, true);
      this.emit(bucket, right[0], right[i], right[i + 1], hr, true);
    }
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      const h = new THREE.Vector3(0, (pts[i][1] + pts[j][1]) / 2 - cy, (pts[i][0] + pts[j][0]) / 2 - cz);
      this.emit(bucket, left[i], left[j], right[j], h, false);
      this.emit(bucket, left[i], right[j], right[i], h, false);
    }
  }

  triangleCount(): number {
    let t = 0;
    for (const b of this.buckets.values()) t += b.pos.length / 9;
    return t;
  }

  /** Triangles per key. */
  triangleCounts(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [k, b] of this.buckets) out[k] = b.pos.length / 9;
    return out;
  }

  /** One merged BufferGeometry per key (insertion order). */
  build(opts: BuildOpts = {}): Record<string, THREE.BufferGeometry> {
    const out: Record<string, THREE.BufferGeometry> = {};
    for (const [key, b] of this.buckets) {
      if (b.pos.length === 0) continue;
      const count = b.pos.length / 3;
      const uv = new Float32Array(count * 2);
      const meters = opts.uvMeters?.[key] ?? 1;
      for (let i = 0; i < count; i++) {
        const nx = Math.abs(b.nor[i * 3]);
        const ny = Math.abs(b.nor[i * 3 + 1]);
        const nz = Math.abs(b.nor[i * 3 + 2]);
        const x = b.pos[i * 3];
        const y = b.pos[i * 3 + 1];
        const z = b.pos[i * 3 + 2];
        if (nx >= ny && nx >= nz) {
          uv[i * 2] = z / meters;
          uv[i * 2 + 1] = y / meters;
        } else if (ny >= nz) {
          uv[i * 2] = x / meters;
          uv[i * 2 + 1] = z / meters;
        } else {
          uv[i * 2] = x / meters;
          uv[i * 2 + 1] = y / meters;
        }
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(b.pos, 3));
      g.setAttribute("normal", new THREE.Float32BufferAttribute(b.nor, 3));
      g.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
      if (opts.vertexColors) g.setAttribute("color", new THREE.Float32BufferAttribute(b.col, 3));
      g.computeBoundingBox();
      g.computeBoundingSphere();
      out[key] = g;
    }
    return out;
  }

  private localMatrix(pos: V3, rot?: V3, extra?: THREE.Quaternion): THREE.Matrix4 {
    const q = new THREE.Quaternion();
    if (rot) q.setFromEuler(new THREE.Euler(rot[0], rot[1], rot[2]));
    if (extra) q.multiply(extra);
    return new THREE.Matrix4().compose(new THREE.Vector3(pos[0], pos[1], pos[2]), q, ONE);
  }

  private begin(pos: V3, opts: PartOpts, extra?: THREE.Quaternion): void {
    this.m.copy(this.cur).multiply(this.localMatrix(pos, opts.rot, extra));
    this.color = opts.color === undefined ? [1, 1, 1] : linearRgb(opts.color);
  }

  private bucket(key: string): Bucket {
    let b = this.buckets.get(key);
    if (b === undefined) {
      b = { pos: [], nor: [], col: [] };
      this.buckets.set(key, b);
    }
    return b;
  }

  /**
   * Emits one triangle given in part space. The winding is flipped so the geometric normal agrees with
   * `hint`; the stored normal is `hint` itself (hintIsNormal) or the geometric normal.
   */
  private emit(
    bucket: Bucket,
    a: THREE.Vector3,
    b: THREE.Vector3,
    c: THREE.Vector3,
    hint: THREE.Vector3,
    hintIsNormal: boolean,
  ): void {
    _e1.subVectors(b, a);
    _e2.subVectors(c, a);
    _n.crossVectors(_e1, _e2);
    if (_n.lengthSq() < 1e-14) return;
    const flip = _n.dot(hint) < 0;
    if (hintIsNormal) _n.copy(hint);
    else if (flip) _n.negate();
    _n.normalize().transformDirection(this.m);
    const second = flip ? c : b;
    const third = flip ? b : c;
    for (const v of [a, second, third]) {
      _p.copy(v).applyMatrix4(this.m);
      bucket.pos.push(_p.x, _p.y, _p.z);
      bucket.nor.push(_n.x, _n.y, _n.z);
      bucket.col.push(this.color[0], this.color[1], this.color[2]);
    }
  }
}

/** Total triangles of a set of geometries. */
export function countTriangles(geos: Iterable<THREE.BufferGeometry>): number {
  let t = 0;
  for (const g of geos) t += (g.index ? g.index.count : (g.getAttribute("position")?.count ?? 0)) / 3;
  return t;
}

/** Disposes every geometry of a built set. */
export function disposeGeometries(geos: Iterable<THREE.BufferGeometry>): void {
  for (const g of geos) g.dispose();
}
